import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings, Plus, Pencil, Trash2, Check, X, Tag, MapPin, Zap, Activity } from 'lucide-react';
import api from '../../services/api';
import { STATUS_CONFIG } from './estoque.utils';

const TABS = [
  { id: 'categorias', label: 'Categorias',   icon: Tag },
  { id: 'obras',      label: 'Obras / Locais', icon: MapPin },
  { id: 'tensoes',    label: 'Tensão',        icon: Zap },
  { id: 'status',     label: 'Status',        icon: Activity },
];

// ─── Linha editável genérica ────────────────────────────────────────────────
function EditableRow({ value, onSave, onDelete, deleteDisabled, deleteTitle }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);

  async function salvar() {
    if (!val.trim()) return;
    setSaving(true);
    await onSave(val.trim());
    setSaving(false);
    setEditing(false);
  }

  return (
    <div className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0 group">
      {editing ? (
        <>
          <input value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && salvar()}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
          <button onClick={salvar} disabled={saving} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg">
            <Check size={16} />
          </button>
          <button onClick={() => { setEditing(false); setVal(value); }} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
            <X size={16} />
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm text-gray-800">{value}</span>
          <button onClick={() => setEditing(true)} className="p-1.5 text-gray-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
            <Pencil size={14} />
          </button>
          <button onClick={onDelete} disabled={deleteDisabled} title={deleteTitle}
            className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed">
            <Trash2 size={14} />
          </button>
        </>
      )}
    </div>
  );
}

// ─── Seção genérica de lista (obras, tensoes) ───────────────────────────────
function ListaSection({ tipo, titulo, placeholder }) {
  const qc = useQueryClient();
  const [novoValor, setNovoValor] = useState('');
  const [adding, setAdding] = useState(false);
  const [erro, setErro] = useState('');

  const { data } = useQuery({
    queryKey: ['estoque-lista', tipo],
    queryFn: () => api.get(`/estoque/config/listas?tipo=${tipo}`).then(r => r.data.data),
  });

  async function adicionar() {
    if (!novoValor.trim()) return;
    setErro('');
    try {
      await api.post('/estoque/config/listas', { tipo, valor: novoValor });
      qc.invalidateQueries(['estoque-lista', tipo]);
      setNovoValor(''); setAdding(false);
    } catch (e) { setErro(e.response?.data?.error || 'Erro'); }
  }

  async function editar(id, valor) {
    await api.put(`/estoque/config/listas/${id}`, { valor });
    qc.invalidateQueries(['estoque-lista', tipo]);
  }

  async function excluir(id) {
    if (!window.confirm('Remover este item?')) return;
    await api.delete(`/estoque/config/listas/${id}`);
    qc.invalidateQueries(['estoque-lista', tipo]);
  }

  const itens = data || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-800">{titulo}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{itens.length} {itens.length === 1 ? 'item' : 'itens'} cadastrados</p>
        </div>
        <button onClick={() => setAdding(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
          <Plus size={15} /> Adicionar
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4">
        {adding && (
          <div className="flex gap-2 mb-3 pb-3 border-b border-gray-100">
            <input value={novoValor} onChange={e => setNovoValor(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && adicionar()}
              placeholder={placeholder} autoFocus
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <button onClick={adicionar} className="px-3 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
              Salvar
            </button>
            <button onClick={() => { setAdding(false); setNovoValor(''); setErro(''); }}
              className="px-3 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        )}
        {erro && <p className="text-xs text-red-600 mb-2">{erro}</p>}
        {itens.length === 0 && !adding && (
          <p className="text-sm text-gray-400 text-center py-4">Nenhum item. Clique em Adicionar.</p>
        )}
        {itens.map(item => (
          <EditableRow
            key={item.id}
            value={item.valor}
            onSave={v => editar(item.id, v)}
            onDelete={() => excluir(item.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Seção de Categorias ────────────────────────────────────────────────────
function CategoriasSection() {
  const qc = useQueryClient();
  const [novoNome, setNovoNome] = useState('');
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editNome, setEditNome] = useState('');
  const [erro, setErro] = useState('');

  const { data } = useQuery({
    queryKey: ['estoque-cats-config'],
    queryFn: () => api.get('/estoque/config/categorias').then(r => r.data.data),
  });

  async function adicionar() {
    if (!novoNome.trim()) return;
    setErro('');
    try {
      await api.post('/estoque/config/categorias', { nome: novoNome });
      qc.invalidateQueries(['estoque-cats-config']);
      qc.invalidateQueries(['estoque-cats']);
      setNovoNome(''); setAdding(false);
    } catch (e) { setErro(e.response?.data?.error || 'Erro'); }
  }

  async function salvarEdicao() {
    await api.put(`/estoque/config/categorias/${editId}`, { nome: editNome });
    qc.invalidateQueries(['estoque-cats-config']);
    qc.invalidateQueries(['estoque-cats']);
    setEditId(null);
  }

  async function excluir(id) {
    if (!window.confirm('Excluir categoria? Itens vinculados ficarão sem categoria.')) return;
    try {
      await api.delete(`/estoque/config/categorias/${id}`);
      qc.invalidateQueries(['estoque-cats-config']);
      qc.invalidateQueries(['estoque-cats']);
    } catch (e) { alert(e.response?.data?.error || 'Erro ao excluir'); }
  }

  const cats = data || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-800">Categorias de Ferramentas</h2>
          <p className="text-xs text-gray-500 mt-0.5">{cats.length} categorias cadastradas</p>
        </div>
        <button onClick={() => setAdding(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
          <Plus size={15} /> Nova categoria
        </button>
      </div>

      {adding && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-blue-800">Nova categoria</p>
          <input value={novoNome} onChange={e => setNovoNome(e.target.value)} placeholder="Nome da categoria"
            onKeyDown={e => e.key === 'Enter' && adicionar()}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
          {erro && <p className="text-xs text-red-600">{erro}</p>}
          <div className="flex gap-2">
            <button onClick={adicionar} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">Salvar</button>
            <button onClick={() => { setAdding(false); setNovoNome(''); setErro(''); }}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50">Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {cats.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Nenhuma categoria cadastrada.</p>}
        {cats.map(cat => (
          <div key={cat.id} className="group flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50">
            {editId === cat.id ? (
              <>
                <input value={editNome} onChange={e => setEditNome(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && salvarEdicao()}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
                <button onClick={salvarEdicao} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"><Check size={16} /></button>
                <button onClick={() => setEditId(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
              </>
            ) : (
              <>
                <span className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold text-blue-600">
                  {cat.nome?.charAt(0)?.toUpperCase() || '?'}
                </span>
                <span className="flex-1 text-sm font-medium text-gray-800">{cat.nome}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditId(cat.id); setEditNome(cat.nome); }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={14} /></button>
                  <button onClick={() => excluir(cat.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Seção de Status (somente leitura — são valores do sistema) ─────────────
function StatusSection() {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-800">Status de Itens</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Os status são definidos pelo sistema e controlam a lógica de movimentações. Não podem ser alterados.
        </p>
      </div>
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {Object.entries(STATUS_CONFIG).map(([valor, cfg]) => (
          <div key={valor} className="flex items-center gap-4 px-4 py-3 border-b border-gray-50 last:border-0">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
              {cfg.label}
            </span>
            <span className="font-mono text-xs text-gray-400">{valor}</span>
            <span className="text-xs text-gray-400 ml-auto">
              {{
                disponivel:  'Item disponível para retirada',
                em_uso:      'Item em uso em obra ou por responsável',
                manutencao:  'Item em manutenção preventiva ou corretiva',
                descartado:  'Item descartado / fora de uso',
                emprestado:  'Item emprestado para terceiros',
              }[valor]}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-3">
        Para alterar o status de um item use a função <strong>Movimentar</strong> na tela de listagem ou detalhe da ferramenta.
      </p>
    </div>
  );
}

// ─── Página principal ───────────────────────────────────────────────────────
export default function EstoqueConfiguracoes() {
  const [tab, setTab] = useState('categorias');

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Settings size={22} className="text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configurações do Estoque</h1>
          <p className="text-sm text-gray-500">Gerencie categorias, locais, tensões e status</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t.id ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'categorias' && <CategoriasSection />}
      {tab === 'obras'      && <ListaSection tipo="obra"   titulo="Obras / Locais"  placeholder="Ex: Ingarden, Leo..." />}
      {tab === 'tensoes'    && <ListaSection tipo="tensao" titulo="Opções de Tensão" placeholder="Ex: 380V trifásico..." />}
      {tab === 'status'     && <StatusSection />}
    </div>
  );
}
